<template>
  <themed-modal
    name="create-task-modal"
    :title="isEditMode ? 'Edit Task' : 'New Task'"
    size="lg"
    :scrollable="true"
    :clickToClose="!loading"
  >
    <div class="create-task-modal">
      <themed-form
        v-if="form"
        :fields="formFields"
        v-model="form"
        :errors="formErrors"
        :loading="loading"
        layout="grid"
        :columns="2"
        @submit="submitTask"
      >
        <!-- Custom date-time field for Start -->
        <template #field-date_start="{ field }">
          <themed-form-field
            :label="field.label"
            :error="formErrors.date_time_start"
            :required="field.required"
            layout="vertical"
            :field-name="field.name"
            class="themed-form__field--col-2"
          >
            <template v-slot="{ fieldId, errorId, hasError }">
              <div class="create-task-modal__date-time">
                <themed-input
                  :id="fieldId"
                  type="date"
                  v-model="form.date_start"
                  :error="hasError"
                  :aria-invalid="hasError ? 'true' : null"
                  :aria-describedby="hasError ? errorId : null"
                />
                <themed-input
                  v-if="!form.is_all_day"
                  type="time"
                  v-model="form.time_start"
                  :error="hasError"
                />
              </div>
            </template>
          </themed-form-field>
        </template>

        <!-- Custom date-time field for End -->
        <template #field-date_end="{ field }">
          <themed-form-field
            :label="field.label"
            :error="formErrors.date_time_end"
            :required="field.required"
            layout="vertical"
            :field-name="field.name"
            class="themed-form__field--col-2"
          >
            <template v-slot="{ fieldId, errorId, hasError }">
              <div class="create-task-modal__date-time">
                <themed-input
                  :id="fieldId"
                  type="date"
                  v-model="form.date_end"
                  :error="hasError"
                  :aria-invalid="hasError ? 'true' : null"
                  :aria-describedby="hasError ? errorId : null"
                />
                <themed-input
                  v-if="!form.is_all_day"
                  type="time"
                  v-model="form.time_end"
                  :error="hasError"
                />
              </div>
            </template>
          </themed-form-field>
        </template>
      </themed-form>
    </div>

    <template #footer>
      <div class="create-task-modal__footer">
        <themed-button
          variant="outline"
          @click="closeModal"
          :disabled="loading"
        >
          Cancel
        </themed-button>
        
        <themed-button
          variant="default"
          @click="submitTask"
          :disabled="loading"
        >
          <span v-if="loading" class="create-task-modal__loading">
            <theme-icon icon="loader" type="icons" classes="w-4 h-4 animate-spin" />
          </span>
          <span v-else>{{ isEditMode ? 'Update Task' : 'Create Task' }}</span>
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemedInput from '../ThemedInput.vue';

export default {
  name: 'CreateTaskModal',
  
  components: {
    ThemedModal,
    ThemedButton,
    ThemeIcon,
    ThemedForm,
    ThemedFormField,
    ThemedInput
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    },
    task: {
      type: Object,
      default: null
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      form: {
        task_category: '',
        is_all_day: false,
        date_start: null,
        time_start: null,
        date_end: null,
        time_end: null,
        priority: '',
        task: '',
        note: '',
        is_complete: false,
        project_id: this.projectId
      },
      formErrors: {},
      loading: false
    };
  },
  
  computed: {
    formFields() {
      return [
        {
          name: 'task_category',
          type: 'select',
          label: 'Type',
          required: true,
          col: 1,
          options: [
            { label: 'Select type...', value: '', disabled: true },
            { label: 'Call', value: 'Call' },
            { label: 'Legal Review', value: 'Legal Review' },
            { label: 'Meeting', value: 'Meeting' },
            { label: 'Presentation', value: 'Presentation' },
            { label: 'Status Update', value: 'Status Update' },
            { label: 'Tour', value: 'Tour' },
            { label: 'Miscellaneous', value: 'Miscellaneous' }
          ]
        },
        {
          name: 'is_all_day',
          type: 'checkbox',
          label: 'All Day Task?',
          col: 1
        },
        {
          name: 'date_start',
          type: 'input',
          inputType: 'date',
          label: 'Start',
          required: true,
          col: 2
        },
        {
          name: 'date_end',
          type: 'input',
          inputType: 'date',
          label: 'End',
          required: true,
          col: 2
        },
        {
          name: 'priority',
          type: 'select',
          label: 'Priority',
          required: true,
          col: 1,
          options: [
            { label: 'Select priority...', value: '', disabled: true },
            { label: 'Low', value: 'Low' },
            { label: 'Medium', value: 'Medium' },
            { label: 'High', value: 'High' },
            { label: 'Urgent', value: 'Urgent' }
          ]
        },
        {
          name: 'is_complete',
          type: 'checkbox',
          label: 'Completed?',
          col: 1
        },
        {
          name: 'task',
          type: 'input',
          label: 'Task',
          placeholder: 'Enter task description...',
          required: true,
          col: 2
        },
        {
          name: 'note',
          type: 'textarea',
          label: 'Note',
          placeholder: 'Add additional notes...',
          rows: 4,
          col: 2
        }
      ];
    }
  },
  
  watch: {
    task: {
      handler(newTask) {
        if (newTask && this.isEditMode) {
          this.resetForm();
        }
      },
      immediate: true
    },
    isEditMode(newValue) {
      if (!newValue) {
        this.resetForm();
      }
    }
  },
  
  mounted() {
    // Listen for modal opened event to reset/populate form
    this.$root.$on('modal::opened', (modalName) => {
      if (modalName === 'create-task-modal') {
        this.resetForm();
      }
    });
  },
  
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('modal::opened');
  },
  
  methods: {
    resetForm() {
      if (this.isEditMode && this.task) {
        // Populate form with existing task data
        this.form = {
          task_category: this.task.task_category || '',
          is_all_day: this.task.is_all_day || false,
          date_start: this.task.start_date_formatted || null,
          time_start: this.task.start_time_formatted || null,
          date_end: this.task.end_date_formatted || null,
          time_end: this.task.end_time_formatted || null,
          priority: this.task.priority || '',
          task: this.task.task || '',
          note: this.task.note || '',
          is_complete: this.task.is_complete || false,
          project_id: this.projectId
        };
      } else {
        // Reset to empty form for create mode
        this.form = {
          task_category: '',
          is_all_day: false,
          date_start: null,
          time_start: null,
          date_end: null,
          time_end: null,
          priority: '',
          task: '',
          note: '',
          is_complete: false,
          project_id: this.projectId
        };
      }
      this.formErrors = {};
    },
    
    validateForm() {
      this.formErrors = {};
      let isValid = true;
      
      if (!this.form.task_category) {
        this.formErrors.task_category = 'Task category is required';
        isValid = false;
      }
      
      if (!this.form.date_start) {
        this.formErrors.date_time_start = 'Start date is required';
        isValid = false;
      }
      
      if (!this.form.date_end) {
        this.formErrors.date_time_end = 'End date is required';
        isValid = false;
      }
      
      if (!this.form.priority) {
        this.formErrors.priority = 'Priority is required';
        isValid = false;
      }
      
      if (!this.form.task || this.form.task.trim() === '') {
        this.formErrors.task = 'Task description is required';
        isValid = false;
      }
      
      // Validate start date is before or equal to end date
      if (this.form.date_start && this.form.date_end) {
        const startDate = new Date(this.form.date_start);
        const endDate = new Date(this.form.date_end);
        
        if (startDate > endDate) {
          this.formErrors.date_time_end = 'End date must be after start date';
          isValid = false;
        }
      }
      
      return isValid;
    },
    
    buildDateTime(date, time) {
      if (!date) return null;
      
      if (this.form.is_all_day || !time) {
        return date;
      }
      
      return `${date} ${time}`;
    },
    
    async submitTask() {
      if (!this.validateForm()) {
        return;
      }
      
      this.loading = true;
      this.formErrors = {};
      
      try {
        // Build datetime strings
        const payload = {
          ...this.form,
          date_time_start: this.buildDateTime(this.form.date_start, this.form.time_start),
          date_time_end: this.buildDateTime(this.form.date_end, this.form.time_end)
        };
        
        // Remove separate date/time fields
        delete payload.date_start;
        delete payload.time_start;
        delete payload.date_end;
        delete payload.time_end;
        
        let response;
        
        if (this.isEditMode && this.task) {
          // Update existing task
          response = await axios.patch(`/project/${this.projectId}/tasks/${this.task.id}`, payload);
          
          // Emit success event with updated task
          this.$emit('task-updated', response.data.task);
        } else {
          // Create new task
          response = await axios.post(`/project/${this.projectId}/tasks`, payload);
          
          // Emit success event with new task
          this.$emit('task-created', response.data.task);
        }
      } catch (error) {
        console.error('Error creating task:', error);
        
        if (error.response?.data?.errors) {
          // Map server errors to formErrors (convert arrays to strings)
          const serverErrors = error.response.data.errors;
          Object.keys(serverErrors).forEach(key => {
            this.formErrors[key] = Array.isArray(serverErrors[key]) 
              ? serverErrors[key][0] 
              : serverErrors[key];
          });
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to create task'
          });
        }
      } finally {
        this.loading = false;
      }
    },
    
    closeModal() {
      if (!this.loading) {
        this.$modal.hide('create-task-modal');
      }
    }
  }
};
</script>
